/* =====================================================================
 * DocumentPage — 문서 작성 메인 페이지
 *
 * 사이드바에서 문서를 선택하면 이 페이지에서 에디터가 표시됩니다.
 * 문서가 없으면 새 문서 생성 안내를 표시합니다.
 * ===================================================================== */

import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FileText } from 'lucide-react';
import { useDocumentStore } from '@auw/stores';
import { useChatStore } from '@auw/stores';
import { EmptyState } from '@auw/workspace-ui';
import { DocumentEditor } from './DocumentEditor';

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const DocumentPage: React.FC = () => {
  const activeConversationId = useChatStore((s) => s.activeId);
  const conversations = useChatStore((s) => s.conversations);
  const renameConversation = useChatStore((s) => s.renameConversation);

  const documents = useDocumentStore((s) => s.documents);
  const ensureDocument = useDocumentStore((s) => s.ensureDocument);
  const updateTitle = useDocumentStore((s) => s.updateTitle);

  const activeConversation = activeConversationId ? conversations[activeConversationId] : null;
  const isDocumentPage = Boolean(
    activeConversation
    && activeConversation.mode === 'document'
    && activeConversation.workspaceKind === 'page',
  );

  useEffect(() => {
    if (!isDocumentPage || !activeConversation) return;
    ensureDocument(activeConversation.id, activeConversation.title);
  }, [ensureDocument, isDocumentPage, activeConversation?.id, activeConversation?.title]);

  const activeDoc = isDocumentPage && activeConversation
    ? documents[activeConversation.id] ?? null
    : null;
  const activePageConversation = isDocumentPage && activeConversation ? activeConversation : null;

  /* ── 문서 제목 변경 ── */
  const handleTitleChange = useCallback(
    (title: string) => {
      if (!activePageConversation) return;
      renameConversation(activePageConversation.id, title);
      updateTitle(activePageConversation.id, title);
    },
    [activePageConversation, renameConversation, updateTitle],
  );

  if (!isDocumentPage) {
    return (
      <EmptyState
        icon={<FileText size={40} />}
        title="문서 페이지를 선택하세요"
        description="왼쪽 사이드바에서 문서 페이지를 선택하면 내용이 표시됩니다."
      />
    );
  }

  if (!activeDoc) {
    return (
      <EmptyState
        icon={<FileText size={40} />}
        title="문서를 불러오는 중..."
      />
    );
  }

  if (!activePageConversation) {
    return null;
  }

  return (
    <PageWrap>
      <DocumentEditor
        key={activePageConversation.id}
        documentId={activePageConversation.id}
        initialContent={activeDoc.content}
        title={activePageConversation.title || activeDoc.title}
        onTitleChange={handleTitleChange}
      />
    </PageWrap>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const PageWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

